import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { drrDoc, serverGroupsDoc } from 'app/docLinks'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import { default as PicklistField } from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Slider from 'core/elements/Slider'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useListAction from 'core/hooks/useListAction'
import { yamlValidator } from 'core/utils/fieldValidators'
import StretchedClusterPicklist from 'openstack/components/infrastructure/stretched-clusters/components/StretchedClusterPicklist'
import { stretchedClustersByClusterSelector } from 'openstack/components/infrastructure/stretched-clusters/selectors'
import { NetworkMode } from '../../networks/constants'
import { listSecurityGroups } from '../../networks/security-groups/actions'
import { securityGroupsSelector } from '../../networks/security-groups/selectors'
import {
  countValidator,
  getDisableMultipleCreateVmsState,
} from '../CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import { getSecurityGroupAutoState, securityGroupColumns } from '../helpers'
import { ENABLE_SIMPLE_NETWORK_BATCH_CREATION } from '../hooks/useNetworkPorts'
import MigrationPriorityPicklist from '../MigrationPriorityPicklist'
import SshKeyPicklist from '../SshKeyPicklist'
import ServerGroupsPicklist from '../server-groups/ServerGroupsPicklist'

interface Props {
  getParamsUpdater: any
  loadingSecurityGroups: boolean
  minAvailableIpsNetwork: any
  params: any
  securityGroupsList: any[]
  updateParams: any
  vmNetworkMode: NetworkMode | null
}

export function useCreateVirtualMachineCustomizeStepController() {
  const { loading: loadingSecurityGroups } = useListAction(listSecurityGroups, {
    params: {},
  })
  const securityGroupsList = useSelector(securityGroupsSelector)

  return {
    loadingSecurityGroups,
    securityGroupsList,
  }
}

export default function CreateVirtualMachineCustomizeStep({
  getParamsUpdater,
  loadingSecurityGroups,
  minAvailableIpsNetwork,
  params,
  securityGroupsList,
  updateParams,
  vmNetworkMode,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const disableMultipleVMs = getDisableMultipleCreateVmsState(
    params,
    ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  )
  const stretchedClustersByCluster = useSelector(stretchedClustersByClusterSelector)
  const clusterName = params.availabilityZone || ''
  const clusterPair = stretchedClustersByCluster[clusterName]
  const hasStretchedClusters = !!(clusterPair?.domainA || clusterPair?.domainB)

  // Extract primary domain ID from the stored `primaryId:secondaryId` value
  const selectedDomainId = params.stretchedCluster?.split(':')?.[0] || ''

  // When user picks a domain ID, build the `primaryId:secondaryId` format
  const handleStretchedClusterChange = useCallback(
    (domainId: string) => {
      if (!domainId || !clusterPair) {
        updateParams({ stretchedCluster: '' })
        return
      }
      let secondaryId = ''
      if (String(clusterPair.domainA?.id) === domainId) {
        secondaryId = String(clusterPair.domainB?.id || '')
      } else {
        secondaryId = String(clusterPair.domainA?.id || '')
      }
      updateParams({ stretchedCluster: `${domainId}:${secondaryId}` })
    },
    [clusterPair, updateParams],
  )

  // FR-011, FR-012, FR-013: Work out how the security-group control should behave
  // based on the networks/ports the user picked — e.g. whether it's turned off,
  // forced on, and which groups are pre-selected or locked. (Security groups are
  // firewall rule sets applied to a VM's network interfaces.)
  const sgAutoState = useMemo(
    () =>
      getSecurityGroupAutoState(
        params.networks,
        params.selectedPorts,
        securityGroupsList,
        params.networkAttachments,
      ),
    [params.networks, params.selectedPorts, securityGroupsList, params.networkAttachments],
  )

  // Remembers what the network/port selection looked like last time, so we can tell
  // when it actually changed and avoid re-running the auto-update on every render.
  const prevNetworkKeyRef = useRef<string>('')

  useEffect(() => {
    if (loadingSecurityGroups) return

    // Make one string that fingerprints the current selection: each network's id,
    // whether its port security is on, and the chosen port(s) — taken per NIC row if
    // rows exist, otherwise from the older single-port map. If this string is the same
    // as last time, nothing relevant changed.
    const networkKey = (params.networks || [])
      .map((n: any) => {
        const rowPorts = (params.networkAttachments?.[n.id] || [])
          .map((row: any) => row.port)
          .join(',')
        return `${n.id}:${n.port_security_enabled}:${
          rowPorts || params.selectedPorts?.[n.id] || ''
        }`
      })
      .join('|')

    // Bail out unless the network/port selection actually changed
    if (networkKey === prevNetworkKeyRef.current) return
    prevNetworkKeyRef.current = networkKey

    if (sgAutoState.toggleState === 'disabled') {
      updateParams({ useSecurityGroups: false, securityGroups: [] })
    } else if (sgAutoState.toggleState === 'lockedOn') {
      updateParams({
        useSecurityGroups: true,
        securityGroups: sgAutoState.autoSelectedGroups,
      })
    }
    // Note: we deliberately leave sgAutoState.autoSelectedGroups out of the deps below.
    // It's computed from params.networks, params.selectedPorts and securityGroupsList,
    // which are already listed, so nothing is missed. It's a fresh array each render, so
    // listing it would make this effect re-run forever (an infinite loop).
  }, [
    params.networks,
    params.selectedPorts,
    params.networkAttachments,
    securityGroupsList,
    loadingSecurityGroups,
    sgAutoState.toggleState,
    updateParams,
  ])

  const handleLockedSecurityGroupsChange = useCallback(
    (newSelection: any[]) => {
      // FR-013: The user's new selection must always keep the locked groups (the ones
      // a chosen port forces on the VM). Start with those locked groups, then add the
      // user's other picks, skipping any that are already locked to avoid duplicates.
      const lockedIds = new Set(sgAutoState.lockedSecurityGroups.map((sg: any) => sg.id))
      const withLocked = [
        ...sgAutoState.lockedSecurityGroups,
        ...newSelection.filter((sg: any) => !lockedIds.has(sg.id)),
      ]
      getParamsUpdater('securityGroups')(withLocked)
    },
    [getParamsUpdater, sgAutoState.lockedSecurityGroups],
  )

  return (
    <div className={classes.fields}>
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Customize VM</Text>
          <Text variant="body2">
            Set advanced options like SSH keys, user data, security groups, VM metadata and optional
            GPU lease time.
          </Text>
        </div>
      </Info>

      {minAvailableIpsNetwork?.availablePortAndIPs < params.count &&
        vmNetworkMode === NetworkMode.MANAGED && (
          <Alert
            variant="warning"
            message={
              <Text variant="body2">
                Not enough available IPs or ports. Managed Network
                <Text variant="caption1"> {minAvailableIpsNetwork?.name} </Text>
                only contains {minAvailableIpsNetwork?.availablePortAndIPs} available IPs or ports.
                Please select another network or select VM count between 1 and{' '}
                {minAvailableIpsNetwork?.availablePortAndIPs}.
              </Text>
            }
          />
        )}
      {disableMultipleVMs.disabled && (
        <Alert
          variant="primary"
          message={<Text variant="body2">{disableMultipleVMs.message}</Text>}
        />
      )}

      <div>
        <Text
          variant="body2"
          component="div"
          className={clsx(classes.tooltipContainer, {
            [classes.disabledText]: disableMultipleVMs.disabled,
          })}
        >
          Number of VMs
          <HintTooltip message="Specify the number of VMs you’d like to create using this configuration" />
        </Text>
        <div
          className={clsx(classes.vmCount, {
            [classes.vmCountDisabled]: disableMultipleVMs.disabled,
          })}
        >
          <Slider
            min={1}
            max={disableMultipleVMs.disabled ? 1 : 20}
            minLabel="1 VM"
            maxLabel="20 VMs"
            step={1}
            onChange={(value) =>
              updateParams({
                count: value,
              })
            }
            value={params.count}
            disabled={disableMultipleVMs.disabled}
          />
          <div className={classes.vmCountInput}>
            <TextField
              id="count"
              label=""
              onChange={(value) =>
                updateParams({
                  count: value,
                })
              }
              value={params.count}
              type="number"
              min={1}
              max={20}
              disabled={disableMultipleVMs.disabled}
              validations={[countValidator]}
              required
            />
            <Text
              variant="body2"
              className={clsx(classes.vmCountText, {
                [classes.disabledText]: disableMultipleVMs.disabled,
              })}
            >
              VMs
            </Text>
          </div>
        </div>
      </div>
      <div className={classes.halfColumns}>
        <PicklistField
          id="sshKey"
          DropdownComponent={SshKeyPicklist}
          value={params.sshKey}
          onChange={getParamsUpdater('sshKey')}
          // @ts-ignore
          info={
            <div>Select the ssh key that will be injected into the VM to enable ssh access</div>
          }
        />
        <PicklistField
          id="serverGroup"
          DropdownComponent={ServerGroupsPicklist}
          value={params.serverGroup}
          onChange={getParamsUpdater('serverGroup')}
          info={
            <div>
              Select what VM affinity or anti-affinity group this VM should be part of.{' '}
              <ExternalLink className={classes.tooltipLink} url={serverGroupsDoc}>
                More info
              </ExternalLink>
            </div>
          }
        />
      </div>
      {hasStretchedClusters && (
        <>
          <div className={classes.separator} />
          <Text variant="subtitle2">Failure Domain</Text>
          <Text variant="body2">
            Preferred failure domain for this VM. PCD keeps the VM here during normal operation and
            recovers it in the peer domain if this site fails. This is a soft (&quot;should&quot;)
            affinity, not a hard pin.
          </Text>
          <Text variant="body2">
            Available because <strong>{clusterName}</strong> is a stretched cluster.
          </Text>
          <StretchedClusterPicklist
            clusterName={clusterName}
            value={selectedDomainId}
            onChange={handleStretchedClusterChange}
            label="Failure Domain"
            placeholder="None (place anywhere in the cluster)"
            info="Placement preference within the stretched cluster. Leave as None to let PCD use any host."
          />
        </>
      )}
      <div className={classes.separator} />
      <ToggleSwitchField
        id="useCloudInit"
        value={params.useCloudInit}
        onChange={getParamsUpdater('useCloudInit')}
        label="Use Cloud-init"
        disabled={params.sourceType === 'installFromIso'}
        tooltipMsg={
          params.sourceType === 'installFromIso'
            ? 'ISO images do not include a cloud-init agent, so cloud-init configuration is not available during installation.'
            : undefined
        }
        textDescription={
          'Add user data for initial VM setup, such as installing packages, or configuring users. This will run on first boot.'
        }
      />
      {params.useCloudInit && (
        <div className={classes.tabbed}>
          <CodeMirror
            variant="light"
            label="Cloud-init YAML"
            id="cloudInitYaml"
            showCopyButton
            onChange={getParamsUpdater('cloudInitYaml')}
            value={params.cloudInitYaml}
            validations={[yamlValidator]}
          />
        </div>
      )}
      {/* PCD-8004: Security groups are not applicable to L2 (Layer 2 / Simple)
          networks, so the entire control is hidden in Simple network mode. */}
      {vmNetworkMode !== NetworkMode.SIMPLE && (
        <>
          <ToggleSwitchField
            id="useSecurityGroups"
            value={params.useSecurityGroups}
            onChange={getParamsUpdater('useSecurityGroups')}
            label="Assign Security Groups"
            disabled={
              sgAutoState.toggleState === 'disabled' || sgAutoState.toggleState === 'lockedOn'
            }
            textDescription={
              'Choose one or more security groups to be applied to the VM, to ensure consistent traffic rules across all network interfaces attached to the VM.'
            }
            tooltipMsg={sgAutoState.tooltipMsg}
          />
          {params.useSecurityGroups && (
            <div className={classes.tabbed}>
              {sgAutoState.lockedSecurityGroups.length > 0 && sgAutoState.tooltipMsg && (
                <Alert variant="warning">
                  <Text variant="body2">{sgAutoState.tooltipMsg}</Text>
                </Alert>
              )}
              <ListTableField
                id="securityGroups"
                data={securityGroupsList}
                loading={loadingSecurityGroups}
                columns={securityGroupColumns}
                onChange={
                  sgAutoState.lockedSecurityGroups.length > 0
                    ? handleLockedSecurityGroupsChange
                    : getParamsUpdater('securityGroups')
                }
                value={params.securityGroups}
                uniqueIdentifier="id"
                searchTargets={['name']}
                multiSelection
                required
              />
            </div>
          )}
        </>
      )}
      <div className={classes.separator} />
      <Text variant="subtitle2">Set Migration Priority</Text>
      <Text variant="body2">
        The migration priority determines whether the virtual machine qualifies as a candidate for
        migration by the DRR service, and, if it does, specifies the priority level for its
        migration. <ExternalLink url={drrDoc}>More info</ExternalLink>
      </Text>
      <PicklistField
        id="priority"
        DropdownComponent={MigrationPriorityPicklist}
        value={params.priority}
        onChange={getParamsUpdater('priority')}
      />
      <div className={classes.separator} />
      <KeyValuesField
        id="metadata"
        label="Metadata"
        value={params.metadata}
        onChange={getParamsUpdater('metadata')}
        addLabel="Add Metadata"
        allowMultipleValues
      />
    </div>
  )
}
